// ─────────────────────────────────────────────────────────────
// Centralized API endpoints
//
// The backend URL is picked automatically by CRA based on how you run:
//   • `npm start`      → reads .env.development → local backend on :5007
//   • `npm run build`  → reads .env.production  → VPS backend
//
// If you need to force a specific backend regardless of dev/prod, create
//   frontend/.env.local        (git-ignored, wins over dev/prod files)
// and put:  REACT_APP_API_BASE=http://localhost:5007/
//
// The FALLBACK below is only used if none of the .env files load — it
// keeps the app usable if someone accidentally deletes an env file.
// ─────────────────────────────────────────────────────────────
const FALLBACK = 'http://localhost:5007/';

export const BASE = process.env.REACT_APP_API_BASE || FALLBACK;
export const ASSETS = BASE.replace(/\/+$/, '');

// Public
export const healthUrl = BASE + 'health';
export const facilityStatusUrl = BASE + 'facilityStatus';
export const facilityUrl = BASE + 'facility';
export const facilitySettingsUrl = BASE + 'facility/settings';
export const businessTypesUrl = BASE + 'businessTypes';
export const uploadLogoUrl = BASE + 'uploadLogo';
export const setupFacilityUrl = BASE + 'setupFacility';
export const loginUrl = BASE + 'login';
export const changePasswordUrl = BASE + 'changepassword';

// Branches
export const branchesUrl = BASE + 'branches';
export const branchByIdUrl = (id) => `${BASE}branches/${id}`;
export const branchStatusUrl = (id) => `${BASE}branches/${id}/status`;

// Dashboard
export const overviewUrl = BASE + 'overview';
export const overviewBranchesUrl = BASE + 'overview/branches';
export const overviewActivityUrl = BASE + 'overview/activity';



// Users, roles, permissions
export const usersUrl = BASE + 'users';
export const userByIdUrl = (id) => `${BASE}users/${id}`;
export const userStatusUrl = (id) => `${BASE}users/${id}/status`;
export const userResetPwdUrl = (id) => `${BASE}users/${id}/reset-password`;
export const userPermsUrl = (id) => `${BASE}users/${id}/permissions`;
export const rolesUrl = BASE + 'roles';
export const roleByIdUrl = (id) => `${BASE}roles/${id}`;
export const rolePermissionsUrl = (id) => `${BASE}roles/${id}/permissions`;
export const permissionsUrl = BASE + 'permissions';

// Catalog
export const categoriesUrl = BASE + 'categories';
export const categoryByIdUrl = (id) => `${BASE}categories/${id}`;
export const branchPricingUrl = BASE + 'branch-pricing';
export const productsUrl = BASE + 'products';
export const productByIdUrl = (id) => `${BASE}products/${id}`;
export const nextSkuUrl = BASE + 'products/next-sku';
export const restampPricesUrl = BASE + 'products/restamp-prices';
export const uploadProductImageUrl = BASE + 'uploadProductImage';

// Industry templates / starter catalogs
export const starterCatalogPreviewUrl = (typeKey) => `${BASE}starterCatalog/${typeKey}/preview`;
export const loadStarterCatalogUrl = BASE + 'loadStarterCatalog';

// Inventory & sales
export const inventoryUrl = BASE + 'inventory';
export const inventoryConsolidatedUrl = BASE + 'inventory/consolidated';
export const inventoryAdjustUrl = BASE + 'inventory/adjust';
export const inventoryAdjustmentsUrl = BASE + 'inventory/adjustments';
export const inventoryAdjustmentApproveUrl = (id) => `${BASE}inventory/adjustments/${id}/approve`;
export const inventoryAdjustmentRejectUrl  = (id) => `${BASE}inventory/adjustments/${id}/reject`;
export const inventoryAdjustmentCancelUrl  = (id) => `${BASE}inventory/adjustments/${id}/cancel`;
export const openingStockUrl = BASE + 'inventory/opening-stock';
export const inventoryAnalyticsUrl = BASE + 'inventory/analytics';
export const productHistoryUrl = (id) => `${BASE}inventory/product/${id}/history`;
export const customersUrl = BASE + 'customers';
export const customerByIdUrl = (id) => `${BASE}customers/${id}`;

// Invoices — standalone document preparation
export const invoicesUrl = BASE + 'invoices';
export const invoiceByIdUrl = (id) => `${BASE}invoices/${id}`;
export const invoiceStatusUrl = (id) => `${BASE}invoices/${id}/status`;
export const salesUrl = BASE + 'sales';
export const salesSummaryUrl = BASE + 'sales/summary';
export const saleByIdUrl = (id) => `${BASE}sales/${id}`;

// Stock transfers (inter-branch)
export const transfersUrl = BASE + 'transfers';
export const transferByIdUrl    = (id) => `${BASE}transfers/${id}`;
export const transferApproveUrl = (id) => `${BASE}transfers/${id}/approve`;
export const transferDispatchUrl= (id) => `${BASE}transfers/${id}/dispatch`;
export const transferReceiveUrl = (id) => `${BASE}transfers/${id}/receive`;
export const transferRejectUrl  = (id) => `${BASE}transfers/${id}/reject`;
export const transferCancelUrl  = (id) => `${BASE}transfers/${id}/cancel`;
export const uploadTransferDocUrl = BASE + 'uploadTransferDoc';

// Suppliers & purchases
export const suppliersUrl = BASE + 'suppliers';
export const supplierByIdUrl = (id) => `${BASE}suppliers/${id}`;
export const purchasesUrl = BASE + 'purchases';
export const purchaseByIdUrl = (id) => `${BASE}purchases/${id}`;
export const purchaseApproveUrl = (id) => `${BASE}purchases/${id}/approve`;
export const purchaseApproveAllUrl = BASE + 'purchases/approve-all';
export const purchaseReceiveAllUrl = BASE + 'purchases/receive-all';
export const purchaseApproveReceiveAllUrl = BASE + 'purchases/approve-and-receive-all';
export const purchaseRejectUrl = (id) => `${BASE}purchases/${id}/reject`;
export const purchaseReceiveUrl = (id) => `${BASE}purchases/${id}/receive`;

// Expenses, audit, reports
export const expenseCategoriesUrl = BASE + 'expense-categories';
export const expensesUrl = BASE + 'expenses';
export const expenseByIdUrl = (id) => `${BASE}expenses/${id}`;
export const expenseApproveUrl = (id) => `${BASE}expenses/${id}/approve`;
export const auditUrl = BASE + 'audit';

// Attendance (fingerprint bridge)
export const attendanceTodayUrl    = BASE + 'attendance/today';
export const attendanceDevicesUrl  = BASE + 'attendance/devices';
export const attendanceUnlinkedUrl = BASE + 'attendance/unlinked';
export const attendanceLinkUrl     = BASE + 'attendance/link';
export const attendanceReportUrl   = BASE + 'attendance/report';

// HR — employee management
export const hrEmployeesUrl       = BASE + 'api/hr/employees';
export const hrEmployeeByIdUrl    = (id) => `${BASE}api/hr/employees/${id}`;
export const reportSummaryUrl = BASE + 'reports/summary';
export const reportSalesByDayUrl = BASE + 'reports/sales-by-day';
export const reportSalesByBranchUrl = BASE + 'reports/sales-by-branch';
export const reportTopProductsUrl = BASE + 'reports/top-products';
export const reportInventoryUrl = BASE + 'reports/inventory';
export const reportSalesByCashierUrl    = BASE + 'reports/sales-by-cashier';
export const reportExpensesByCategoryUrl = BASE + 'reports/expenses-by-category';
